import React, { Fragment, memo } from 'react';
import type { MemoExoticComponent } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { FireAnalyticsCallback } from '@atlaskit/editor-common/analytics';
import { ACTION, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
import type { EventDispatcher } from '@atlaskit/editor-common/event-dispatcher';
import { usePortalProvider } from '@atlaskit/editor-common/portal';
import type {
	AllEditorPresetPluginTypes,
	EditorPresetBuilder,
} from '@atlaskit/editor-common/preset';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { Transformer } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorFontSize } from '@atlaskit/editor-shared-styles';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type EditorActions from '../actions';
import ErrorBoundary from '../create-editor/ErrorBoundary';
import ReactEditorViewNext from '../create-editor/ReactEditorView';
import type { EditorAppearanceComponentProps } from '../types/editor-appearance-component';
import type { EditorNextProps } from '../types/editor-props';
import EditorContext from '../ui/EditorContext';
import { IntlProviderIfMissingWrapper } from '../ui/IntlProviderIfMissingWrapper/IntlProviderIfMissingWrapper';
import { createFeatureFlagsFromProps } from '../utils/feature-flags-from-props';
import { RenderTracking } from '../utils/performance/components/RenderTracking';
import { EditorInternalContainerCompiled } from './editor-internal-compiled';
import { getBaseFontSize } from './utils/getBaseFontSize';

interface InternalProps {
	AppearanceComponent: React.ComponentType<
		React.PropsWithChildren<EditorAppearanceComponentProps<[]>>
	>;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	editorActions: EditorActions;
	handleAnalyticsEvent: FireAnalyticsCallback;
	handleSave: (view: EditorView) => void;
	onEditorCreated: (instance: {
		eventDispatcher: EventDispatcher;
		transformer?: Transformer<string>;
		view: EditorView;
	}) => void;
	onEditorDestroyed: (_instance: { transformer?: Transformer<string>; view: EditorView }) => void;
	preset: EditorPresetBuilder<string[], AllEditorPresetPluginTypes[]>;
	props: EditorNextProps;
	providerFactory: ProviderFactory;
}

const DEFAULT_VALUE_PROP_TO_IGNORE: Array<keyof EditorNextProps> = ['defaultValue'];

/**
 * EditorInternalComponent is used to capture the common component
 * from the `render` method of `Editor` and share it with `EditorNext`.
 */
export const EditorInternal: MemoExoticComponent<(props: InternalProps) => JSX.Element> = memo(
	({
		props,
		handleAnalyticsEvent,
		createAnalyticsEvent,
		handleSave,
		editorActions,
		providerFactory,
		onEditorCreated,
		onEditorDestroyed,
		preset,
		AppearanceComponent,
	}: InternalProps) => {
		const overriddenEditorProps = {
			...props,
			onSave: props.onSave ? handleSave : undefined,
			// noop all analytic events, even if a handler is still passed.
			analyticsHandler: undefined,
		};

		const featureFlags = createFeatureFlagsFromProps(props.featureFlags);
		const renderTrackingEnabled =
			isExperimentEnabled('platform_editor_enable_rerender_tracking') &&
			!featureFlags.lcmPreventRenderTracking;

		const [portalProviderAPI, PortalRenderer] = usePortalProvider();
		const [nodeViewPortalProviderAPI, NodeViewPortalRenderer] = usePortalProvider();
		const propsToIgnore: Array<keyof EditorNextProps> = isExperimentEnabled(
			'platform_editor_perf_lint_cleanup',
		)
			? DEFAULT_VALUE_PROP_TO_IGNORE
			: ['defaultValue'];

		const baseFontSize = getBaseFontSize(props.appearance, props.contentMode);
		const fontSize = editorFontSize({ theme: { baseFontSize } });

		return (
			<Fragment>
				{renderTrackingEnabled && (
					<RenderTracking
						componentProps={props}
						action={ACTION.RE_RENDERED}
						actionSubject={ACTION_SUBJECT.EDITOR}
						handleAnalyticsEvent={handleAnalyticsEvent}
						propsToIgnore={propsToIgnore}
						useShallow={false}
					/>
				)}
				<ErrorBoundary
					errorTracking={true}
					createAnalyticsEvent={createAnalyticsEvent}
					contextIdentifierProvider={props.contextIdentifierProvider}
					featureFlags={featureFlags}
				>
					<EditorInternalContainerCompiled fontSize={fontSize}>
						<EditorContext editorActions={editorActions}>
							<IntlProviderIfMissingWrapper>
								<Fragment>
									<ReactEditorViewNext
										editorProps={overriddenEditorProps}
										createAnalyticsEvent={createAnalyticsEvent}
										portalProviderAPI={portalProviderAPI}
										nodeViewPortalProviderAPI={nodeViewPortalProviderAPI}
										providerFactory={providerFactory}
										onEditorCreated={onEditorCreated}
										onEditorDestroyed={onEditorDestroyed}
										disabled={props.disabled}
										preset={preset}
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017: this callback closes over the full props object and derived featureFlags; memoization is ineffective because ReactEditorViewNext is not memo()'d and deps (props, featureFlags) change every render
										render={({
											editor,
											view,
											eventDispatcher,
											config,
											dispatchAnalyticsEvent,
											editorRef,
											editorAPI,
										}) => (
											<AppearanceComponent
												innerRef={editorRef}
												editorAPI={editorAPI}
												// Ignored via go/ees005
												// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
												appearance={props.appearance!}
												disabled={props.disabled}
												editorActions={editorActions}
												editorDOMElement={editor}
												editorView={view}
												providerFactory={providerFactory}
												eventDispatcher={eventDispatcher}
												dispatchAnalyticsEvent={dispatchAnalyticsEvent}
												maxHeight={props.maxHeight}
												minHeight={props.minHeight}
												onSave={props.onSave ? handleSave : undefined}
												onCancel={props.onCancel}
												onSSRMeasure={props.onSSRMeasure}
												popupsMountPoint={props.popupsMountPoint}
												popupsBoundariesElement={props.popupsBoundariesElement}
												popupsScrollableElement={props.popupsScrollableElement}
												contentComponents={config.contentComponents}
												contentMode={props.contentMode}
												primaryToolbarComponents={config.primaryToolbarComponents}
												primaryToolbarIconBefore={props.primaryToolbarIconBefore}
												secondaryToolbarComponents={config.secondaryToolbarComponents}
												customContentComponents={props.contentComponents}
												customPrimaryToolbarComponents={props.primaryToolbarComponents}
												customSecondaryToolbarComponents={props.secondaryToolbarComponents}
												contextPanel={props.contextPanel}
												collabEdit={props.collabEdit}
												persistScrollGutter={props.persistScrollGutter}
												enableToolbarMinWidth={
													props.featureFlags?.toolbarMinWidthOverflow != null
														? !!props.featureFlags?.toolbarMinWidthOverflow
														: props.allowUndoRedoButtons
												}
												isEditorModernisationEnabled={props.isEditorModernisationEnabled}
												useStickyToolbar={props.useStickyToolbar}
												featureFlags={featureFlags}
												pluginHooks={config.pluginHooks}
												__livePage={props.__livePage}
												preset={preset}
												UNSAFE_containLayout={props.UNSAFE_containLayout}
											/>
										)}
									/>
									<PortalRenderer />
									<NodeViewPortalRenderer />
								</Fragment>
							</IntlProviderIfMissingWrapper>
						</EditorContext>
					</EditorInternalContainerCompiled>
				</ErrorBoundary>
			</Fragment>
		);
	},
);
